import {
  Button,
  FormField,
  Input,
  Select,
  SelectProps,
  SpaceBetween,
} from '@cloudscape-design/components';
import { SearchQuestionRequest } from 'src/types/helios/Question';
import useUserProfile from 'src/hooks/useUserProfile';
import { useEffect, useMemo, useState } from 'react';
import { useDivision } from 'src/store/division/useDivision';
import { Division } from 'src/types/orbit/Division';
import QuestionCategoryDropdown from '../questionCategory/QuestionCategoryDropdown';

import MultiSelectTags from '../tag/MultiSelectTags';
import SingleSelectDivision from '../division/SingleSelectDivision';
import { useCategories } from 'src/store/categories/useCategories';
import { useTags } from 'src/store/tags/useTags';
import ButtonGroup from '../ButtonGroup/ButtonGroup';
import MultiSelectTemplate from '../template/MultiSelectTemplate';
import { useTemplates } from 'src/store/templates/useTemplates';
import { convertItem } from 'src/utils/generalUtils';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';
import { DEFAULT_LANGUAGE } from 'src/contstant/commonLabels';
import { parseQuestionIds } from 'src/utils/questionUtils';

interface QuestionFilterViewProps {
  defaultSearchParams?: SearchQuestionRequest;
  resetSearchParams?: SearchQuestionRequest; // What to reset to (from parent)
  onFilterChange: (filters: SearchQuestionRequest) => void;
  onClearAll?: () => void;
}

const QuestionFilterView = ({
  onFilterChange,
  defaultSearchParams,
  resetSearchParams,
  onClearAll,
}: QuestionFilterViewProps) => {
  const { getUserBusinessUnitId, getUserProgramId, getLanguage } = useUserProfile();
  const { divisionList, fetcDivisionsApiStatus, loadDivisionsByBusinessUnit } =
    useDivision();
  const { templatesList, loadTemplatesList } = useTemplates();
  const { categoriesList, loadCategories } = useCategories();
  const { loadTags, tagsList } = useTags();

  const initSearchParams: SearchQuestionRequest = {
    businessUnitId: getUserBusinessUnitId(),
    language: DEFAULT_LANGUAGE,
    isActive: undefined,
    categories: undefined,
    divisionName: undefined,
    tagFilters: undefined,
    excludedTagFilters: undefined,
    questionIds: undefined,
    questionIdsText: undefined,
    additionalLanguages: undefined,
    maxResults: undefined,
    nextToken: undefined,
    templateId: undefined,
  };

  // Use defaultSearchParams directly as the source of truth (controlled component)
  const searchParams = defaultSearchParams || initSearchParams;

  useEffect(() => {
    if (getUserBusinessUnitId()) {
      loadDivisionsByBusinessUnit({
        businessUnitId: getUserBusinessUnitId(),
        programId: getUserProgramId(),
        isDeleted: false,
        isActive: true,
      });
    }

    loadCategories({
      businessUnitId: getUserBusinessUnitId()!,
      isActive: true,
    });

    loadTags({
      businessUnitId: getUserBusinessUnitId()!,
      programId: getUserProgramId()!,
    });
    loadTemplatesList({
      program: getUserProgramId(),
      businessUnitId: getUserBusinessUnitId(),
    });
  }, [getUserBusinessUnitId()]);

  const selectedTemplate = useMemo(() => {
    if (
      searchParams.templateId === '' ||
      searchParams.templateId === undefined ||
      searchParams.templateId === null ||
      templatesList.length === 0
    )
      return null;
    return convertItem<AuditTemplate, SelectProps.Option>(
      templatesList.find((data) => searchParams.templateId === data.id)!,
      (item) => ({
        value: item.id,
        label: item.templateName,
      }),
    );
  }, [searchParams.templateId, templatesList]);

  const templateOptions = useMemo(
    () =>
      templatesList.map((template) => ({
        value: template.id,
        label: template.templateName,
      })),
    [templatesList],
  );

  return (
    <SpaceBetween size="l" data-testid="question-filter-container">
      <FormField
        data-testid="activation-status-field"
        label="Activation Status"
        stretch
        description="Filter Active or Inactive questions using this switch."
      >
        <ButtonGroup
          data-testid="activation-status-button-group"
          options={[
            { value: true, label: 'Active', color: '#2e7d32' },
            { value: false, label: 'Inactive', color: '#d32f2f' },
          ]}
          defaultValue={searchParams.isActive}
          onChange={(value?: boolean) => {
            const newParams = {
              ...searchParams,
              isActive: value,
            };
            onFilterChange(newParams);
          }}
        />
      </FormField>
      <FormField data-testid="division-field" label="Division">
        <SingleSelectDivision
          data-testid="division-select"
          divisions={divisionList}
          isLoading={fetcDivisionsApiStatus.isLoading}
          error={fetcDivisionsApiStatus.errorMessage}
          selectedDivision={searchParams.divisionName || ''}
          onItemSelection={(item: Division | null) => {
            const divisionName = item?.divisionName || '';
            const newParams = {
              ...searchParams,
              divisionName: divisionName || undefined,
            };
            onFilterChange(newParams);
          }}
        />
      </FormField>
      <FormField data-testid="template-field" label="Template" stretch>
        <Select
          data-testid="template-select"
          selectedOption={selectedTemplate}
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              templateId: convertItem<SelectProps.Option, string>(
                detail.selectedOption as SelectProps.Option,
                (item) => item.value || '',
              ),
            };
            onFilterChange(newParams);
          }}
          filteringType="auto"
          options={templateOptions}
          placeholder="Select templates"
        />
      </FormField>
      <FormField data-testid="category-field" label="Category">
        <QuestionCategoryDropdown
          data-testid="category-dropdown"
          categories={categoriesList}
          selectedCategory={searchParams.categories}
          onChange={(selectedCategories) => {
            const newParams = {
              ...searchParams,
              categories: selectedCategories || [],
            };
            onFilterChange(newParams);
          }}
        />
      </FormField>
      <FormField data-testid="question-id-field" label="Question IDs" stretch>
        <Input
          data-testid="question-id-input"
          placeholder="Enter comma separated IDs (e.g., 1, 2, 3)"
          value={searchParams.questionIdsText || ''}
          onChange={({ detail }) => {
            const newParams = {
              ...searchParams,
              questionIdsText: detail.value || undefined,
              questionIds: parseQuestionIds(detail.value),
            };
            onFilterChange(newParams);
          }}
        />
      </FormField>
      <FormField data-testid="tags-field" label={'Tags Included'} stretch>
        <MultiSelectTags
          data-testid="tags-multiselect"
          tags={tagsList}
          isLoading={false}
          error={null}
          excludedItems={Object.entries(searchParams.excludedTagFilters || {}).map(
            ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
          )}
          selectedTagValues={Object.entries(searchParams.tagFilters || {}).map(
            ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
          )}
          onItemSelection={(selectedTags) => {
            const tagsMap: Record<string, string[]> = {};
            selectedTags.forEach((tag) => {
              tagsMap[tag.tagGroupName] = tag.tagValues;
            });
            const newParams = {
              ...searchParams,
              tagFilters: Object.entries(tagsMap).length > 0 ? tagsMap : undefined,
            };
            onFilterChange(newParams);
          }}
        />
      </FormField>

      <FormField data-testid="excluded-tags-field" label={'Tags Excluded'} stretch>
        <MultiSelectTags
          data-testid="excluded-tags-multiselect"
          tags={tagsList}
          isLoading={false}
          error={null}
          excludedItems={Object.entries(searchParams.tagFilters || {}).map(
            ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
          )}
          selectedTagValues={Object.entries(searchParams.excludedTagFilters || {}).map(
            ([tagGroupName, tagValues]) => ({ tagGroupName, tagValues }),
          )}
          onItemSelection={(selectedTags) => {
            const tagsMap: Record<string, string[]> = {};
            selectedTags.forEach((tag) => {
              tagsMap[tag.tagGroupName] = tag.tagValues;
            });
            const newParams = {
              ...searchParams,
              excludedTagFilters:
                Object.entries(tagsMap).length > 0 ? tagsMap : undefined,
            };
            onFilterChange(newParams);
          }}
        />
      </FormField>

      <SpaceBetween direction="horizontal" size="xs">
        <Button
          data-testid="reset-button"
          onClick={() => {
            // Reset to resetSearchParams (provided by parent - either shared or default filters)
            onFilterChange(resetSearchParams || initSearchParams);
          }}
        >
          Reset
        </Button>
        <Button
          data-testid="clear-all-button"
          onClick={() => {
            if (onClearAll) {
              onClearAll();
            }
          }}
        >
          Clear All
        </Button>
      </SpaceBetween>
    </SpaceBetween>
  );
};

export default QuestionFilterView;
